// API client cổng khách — mục "Giới thiệu của tôi" (spec 22/08/2026 §9.3).
// Cùng session cookie SĐT với portal đơn hàng: máy chủ suy ra người giới
// thiệu từ phiên, FE KHÔNG được gửi kèm SĐT hay mã của ai khác.
import { serverErrorCode, serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";

const apiBaseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8080/api/v1";

export type PublicReferral = components["schemas"]["PublicReferral"];
export type PublicReferralCommission = components["schemas"]["PublicReferralCommission"];
export type PublicReferralConfig = components["schemas"]["PublicReferralConfig"];
export type PublicReferralPayout = components["schemas"]["PublicReferralPayout"];
export type PublicReferralPayoutInput = components["schemas"]["PublicReferralPayoutInput"];

/** Giữ nguyên mã lỗi + mã HTTP để FE phân nhánh (401 = chưa vào phiên khách). */
export class ReferralApiError extends Error {
  code: string;
  status: number;

  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = "ReferralApiError";
    this.code = code;
    this.status = status;
  }
}

async function referralErrorFromResponse(
  response: Response,
  fallback: string,
): Promise<ReferralApiError> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    // Giữ fallback.
  }
  return new ReferralApiError(serverErrorMessage(body, fallback), serverErrorCode(body) || "unknown", response.status);
}

export async function fetchMyReferral(init?: { signal?: AbortSignal }): Promise<PublicReferral> {
  const response = await fetch(`${apiBaseUrl}/public/referral/me`, {
    credentials: "include",
    signal: init?.signal ?? null,
  });
  if (!response.ok)
    throw await referralErrorFromResponse(response, "Không tải được mục giới thiệu");
  return (await response.json()) as PublicReferral;
}

/** Khách tự nhận mã của mình. Máy chủ chặn nếu chưa có buổi chụp hoàn thành. */
export async function issueMyReferralCode(): Promise<PublicReferral> {
  const response = await fetch(`${apiBaseUrl}/public/referral/me`, {
    method: "POST",
    credentials: "include",
  });
  if (!response.ok) throw await referralErrorFromResponse(response, "Chưa nhận được mã giới thiệu");
  return (await response.json()) as PublicReferral;
}

export async function updateMyReferralPayout(
  input: PublicReferralPayoutInput,
): Promise<PublicReferralPayout> {
  const response = await fetch(`${apiBaseUrl}/public/referral/me/payout`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw await referralErrorFromResponse(response, "Không lưu được STK");
  return (await response.json()) as PublicReferralPayout;
}
